import { useCallback } from 'react';
import { createWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';
import { formatMac } from '@shared/utils/mac';

interface FdbRealtimeMeta {
  lastEventAt: string | null;
  eventCount: number;
  highlightKeys: string[];
}

interface DeviceFdbRealtimeState {
  meta: Record<number, FdbRealtimeMeta>;
  registerEvent: (deviceId: number, key: string, timestamp?: string | null) => void;
  acknowledge: (deviceId: number, keys?: string[]) => void;
  clear: (deviceId?: number) => void;
}

const DEFAULT_META: FdbRealtimeMeta = {
  lastEventAt: null,
  eventCount: 0,
  highlightKeys: [],
};

const MAX_HIGHLIGHT_ITEMS = 20;

export const useDeviceFdbRealtimeStore = createWithEqualityFn<DeviceFdbRealtimeState>()((set) => ({
  meta: {},
  registerEvent: (deviceId, key, timestamp) =>
    set((state) => {
      const current = state.meta[deviceId] ?? DEFAULT_META;
      const nextHighlights = [key, ...current.highlightKeys.filter((value) => value !== key)].slice(
        0,
        MAX_HIGHLIGHT_ITEMS,
      );

      return {
        meta: {
          ...state.meta,
          [deviceId]: {
            lastEventAt: timestamp ?? current.lastEventAt ?? new Date().toISOString(),
            eventCount: current.eventCount + 1,
            highlightKeys: nextHighlights,
          },
        },
      };
    }),
  acknowledge: (deviceId, keys) =>
    set((state) => {
      if (!(deviceId in state.meta)) {
        return state;
      }

      if (!keys || keys.length === 0) {
        return {
          meta: {
            ...state.meta,
            [deviceId]: {
              ...state.meta[deviceId],
              highlightKeys: [],
            },
          },
        };
      }

      const nextHighlights = state.meta[deviceId].highlightKeys.filter(
        (value) => !keys.includes(value),
      );

      return {
        meta: {
          ...state.meta,
          [deviceId]: {
            ...state.meta[deviceId],
            highlightKeys: nextHighlights,
          },
        },
      };
    }),
  clear: (deviceId) =>
    set((state) => {
      if (deviceId == null) {
        return { meta: {} };
      }

      if (!(deviceId in state.meta)) {
        return state;
      }

      const nextMeta = { ...state.meta };
      delete nextMeta[deviceId];
      return { meta: nextMeta };
    }),
}));

export function useDeviceFdbRealtimeMeta(deviceId: number): FdbRealtimeMeta {
  return useDeviceFdbRealtimeStore(
    useCallback((state) => state.meta[deviceId] ?? DEFAULT_META, [deviceId]),
    shallow,
  );
}

export function buildFdbEntryKey(mac: string, port?: number | null, vlan?: number | null): string {
  const safeMac = formatMac(mac);
  const safePort = port != null ? String(port) : 'null';
  const safeVlan = vlan != null ? String(vlan) : 'null';
  return `${safeMac}-${safePort}-${safeVlan}`;
}

